import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from "../../utils/Enums";
import {apiClient} from "../../utils/ApiClient";
import {NewCategoryFormProps} from "../../components/categories/NewCategoryScreen";
import {ModifyCategoryFormProps} from "../../components/categories/ModifyCategoryScreen";
import Category from "../../models/Category";

interface CategorysState {
    categories: Category[];
    currentCategory?: Category | null;
    getCategorysStatus: RequestStatus;
    getCurrentCategory: RequestStatus;
    createCategoryStatus: RequestStatus;
    updateCategoryStatus: RequestStatus;
    deleteCategoryStatus: RequestStatus;
    errors: string[],
}

const initialState: CategorysState = {
    categories: [],
    currentCategory: null,
    getCategorysStatus: RequestStatus.Idle,
    getCurrentCategory: RequestStatus.Idle,
    createCategoryStatus: RequestStatus.Idle,
    updateCategoryStatus: RequestStatus.Idle,
    deleteCategoryStatus: RequestStatus.Idle,
    errors: [],
};

export const getCategories = createAsyncThunk(
    'categories/getCategories',
    async (_, {rejectWithValue}) => {
        const response = await apiClient.get('categories');
        return response.data;
    }
);

export const getCategoriesAll = createAsyncThunk(
    'categories/getCategoriesAll',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('categories/all');
        return response.data;
    }
);

export const createCategory = createAsyncThunk(
    'categories/createCategory',
    async (data: NewCategoryFormProps, {rejectWithValue}) => {
        const response = await apiClient.post('categories', data);
        return response.data;
    }
);

export const getCurrentCategory = createAsyncThunk(
    'categories/getCurrentCategory',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`categories/${id}`);
        return response.data;
    }
);

export const updateCategory = createAsyncThunk(
    'categories/updateCategory',
    async ({id, data}: { id: string | undefined, data: ModifyCategoryFormProps }, {rejectWithValue}) => {
        const response = await apiClient.put(`categories/${id}`, data);
        return response.data;
    }
);

export const deleteCategory = createAsyncThunk(
    'categories/deleteCategory',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.delete(`categories/${id}`);
        return response.data;
    }
);

const categoriesSlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {
        resetCreateCategoryStatus: (state) => {
            state.createCategoryStatus = RequestStatus.Idle;
            state.currentCategory = null;
        },
        resetUpdateCategoryStatus: (state) => {
            state.updateCategoryStatus = RequestStatus.Idle;
            state.currentCategory = null;
        },
        resetDeleteCategoryStatus: (state) => {
            state.deleteCategoryStatus = RequestStatus.Idle;
            state.currentCategory = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCategories.pending, (state, action) => {
                state.getCategorysStatus = RequestStatus.Loading;
            })
            .addCase(getCategories.fulfilled, (state, action) => {
                state.getCategorysStatus = RequestStatus.Success;
                state.categories = action.payload;
            })
            .addCase(getCategories.rejected, (state, action) => {
                state.getCategorysStatus = RequestStatus.Failed;
            })

            .addCase(createCategory.pending, (state, action) => {
                state.createCategoryStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createCategory.fulfilled, (state, action) => {
                state.createCategoryStatus = RequestStatus.Success;
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.createCategoryStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateCategory.pending, (state, action) => {
                state.updateCategoryStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                state.updateCategoryStatus = RequestStatus.Success;
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.updateCategoryStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteCategory.pending, (state, action) => {
                state.deleteCategoryStatus = RequestStatus.Loading;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.deleteCategoryStatus = RequestStatus.Success;
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.deleteCategoryStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentCategory.pending, (state, action) => {
                state.getCurrentCategory = RequestStatus.Loading;
            })
            .addCase(getCurrentCategory.fulfilled, (state, action) => {
                state.getCurrentCategory = RequestStatus.Success;
                state.currentCategory = action.payload;
            })
            .addCase(getCurrentCategory.rejected, (state, action) => {
                state.getCurrentCategory = RequestStatus.Failed;
            })

            .addCase(getCategoriesAll.pending, (state) => {
                state.getCategorysStatus = RequestStatus.Loading;
            })
            .addCase(getCategoriesAll.fulfilled, (state, action) => {
                state.getCategorysStatus = RequestStatus.Success;
                state.categories = action.payload;
            })
            .addCase(getCategoriesAll.rejected, (state, action) => {
                state.getCategorysStatus = RequestStatus.Failed;
                // Handle the error as needed
            });

    },
});

export const {
    resetCreateCategoryStatus,
    resetUpdateCategoryStatus,
    resetDeleteCategoryStatus
} = categoriesSlice.actions;
export default categoriesSlice.reducer;